import {
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

import { ActionConfirmationHost } from "./ActionConfirmation.js";
import { DialogSurfacePortalContext } from "./DialogSurfacePortalContext.js";

export type DialogPresentation = "dialog" | "drawer";
export type DialogSize = "small" | "medium" | "large" | "wide";

type DialogRole = "dialog" | "alertdialog";

export interface DialogProps {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  children?: ReactNode;
  onClose: () => void;
  initialFocusRef?: RefObject<HTMLElement>;
  /** Stable destination when the action that opened the dialog is replaced or removed. */
  restoreFocusRef?: RefObject<HTMLElement>;
  size?: DialogSize;
  presentation?: DialogPresentation;
  dismissOnBackdrop?: boolean;
  dismissOnEscape?: boolean;
  showCloseButton?: boolean;
  busy?: boolean;
  closeLabel?: string;
  closeButtonText?: string;
  role?: DialogRole;
  className?: string;
}

interface InertState {
  count: number;
  hadAttribute: boolean;
}

const inertStates = new Map<HTMLElement, InertState>();
const openLayers: HTMLElement[] = [];
let bodyLockCount = 0;
let bodyOverflowBeforeLock = "";

function classes(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(" ");
}

function acquireBodyLock() {
  if (bodyLockCount === 0) {
    bodyOverflowBeforeLock = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  bodyLockCount += 1;
}

function releaseBodyLock() {
  bodyLockCount = Math.max(0, bodyLockCount - 1);
  if (bodyLockCount === 0) {
    document.body.style.overflow = bodyOverflowBeforeLock;
  }
}

function acquireInert(element: HTMLElement) {
  const current = inertStates.get(element);
  if (current) {
    current.count += 1;
    return;
  }

  inertStates.set(element, {
    count: 1,
    hadAttribute: element.hasAttribute("inert"),
  });
  element.setAttribute("inert", "");
}

function releaseInert(element: HTMLElement) {
  const current = inertStates.get(element);
  if (!current) return;
  current.count -= 1;
  if (current.count > 0) return;

  if (!current.hadAttribute) element.removeAttribute("inert");
  inertStates.delete(element);
}

function inertBackground(layer: HTMLElement): () => void {
  const acquired = new Set<HTMLElement>();
  let child: HTMLElement = layer;
  let parent = child.parentElement;

  while (parent) {
    for (const sibling of Array.from(parent.children)) {
      if (!(sibling instanceof HTMLElement) || sibling === child) continue;
      // Transitioning or nested modal layers must never inert one another.
      if (sibling.dataset.uiDialogLayer === "true") continue;
      acquireInert(sibling);
      acquired.add(sibling);
    }
    if (parent === document.body) break;
    child = parent;
    parent = parent.parentElement;
  }

  return () => {
    for (const element of acquired) releaseInert(element);
  };
}

function registerLayer(layer: HTMLElement): () => void {
  openLayers.push(layer);
  return () => {
    const index = openLayers.indexOf(layer);
    if (index >= 0) openLayers.splice(index, 1);
  };
}

function topLayer(): HTMLElement | null {
  const connected = openLayers.filter((layer) => layer.isConnected);
  if (connected.length === 0) return null;

  return connected.reduce((top, candidate) => {
    if (top === candidate) return top;
    if (top.contains(candidate)) return candidate;
    if (candidate.contains(top)) return top;
    const position = top.compareDocumentPosition(candidate);
    return position & Node.DOCUMENT_POSITION_FOLLOWING ? candidate : top;
  });
}

function focusableElements(surface: HTMLElement): HTMLElement[] {
  return Array.from(
    surface.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute("hidden") && element.getAttribute("aria-hidden") !== "true");
}

function focusInside(
  surface: HTMLElement,
  initialFocusRef: RefObject<HTMLElement> | undefined,
  closeButton: HTMLButtonElement | null,
) {
  const requested = initialFocusRef?.current;
  if (requested && surface.contains(requested) && !requested.matches(":disabled")) {
    requested.focus();
    return;
  }

  if (closeButton && !closeButton.disabled) {
    closeButton.focus();
    return;
  }

  const first = focusableElements(surface)[0];
  (first ?? surface).focus();
}

export function Dialog({
  title,
  description,
  eyebrow,
  children,
  onClose,
  initialFocusRef,
  restoreFocusRef,
  size = "medium",
  presentation = "dialog",
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  showCloseButton = true,
  busy = false,
  closeLabel = "Close",
  closeButtonText = "×",
  role = "dialog",
  className,
}: DialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const layerRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(busy);
  const dismissOnEscapeRef = useRef(dismissOnEscape);
  const dismissOnBackdropRef = useRef(dismissOnBackdrop);
  const hasDescription = Boolean(description);

  useEffect(() => {
    onCloseRef.current = onClose;
    busyRef.current = busy;
    dismissOnEscapeRef.current = dismissOnEscape;
    dismissOnBackdropRef.current = dismissOnBackdrop;
  });

  useEffect(() => {
    const layer = layerRef.current;
    const surface = surfaceRef.current;
    if (!layer || !surface) return;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const unregisterLayer = registerLayer(layer);
    const releaseBackground = inertBackground(layer);
    acquireBodyLock();
    focusInside(surface, initialFocusRef, closeButtonRef.current);

    const onKeyDown = (event: KeyboardEvent) => {
      if (topLayer() !== layer) return;

      if (event.key === "Escape") {
        // Preserve the event semantics feature-owned dialogs had before this
        // consolidation. Nested controls such as DateField keep their own
        // document-level Escape handling rather than the shell swallowing it.
        if (!busyRef.current && dismissOnEscapeRef.current) onCloseRef.current();
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = focusableElements(surface);
      if (focusable.length === 0) {
        event.preventDefault();
        surface.focus();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      const outside = !(active instanceof Node) || !surface.contains(active);

      if (event.shiftKey && (outside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (outside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    };

    const onFocusIn = (event: FocusEvent) => {
      if (topLayer() !== layer) return;
      const target = event.target;
      if (target instanceof Node && surface.contains(target)) return;
      focusInside(surface, initialFocusRef, closeButtonRef.current);
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      unregisterLayer();
      releaseBackground();
      releaseBodyLock();
      const previousCanReceiveFocus = previouslyFocused?.isConnected
        && previouslyFocused !== document.body
        && previouslyFocused !== document.documentElement
        && !previouslyFocused.closest("[inert]")
        && !previouslyFocused.matches(":disabled")
        && !previouslyFocused.matches(".ui-dialog-surface")
        && (previouslyFocused.tabIndex >= 0 || previouslyFocused.hasAttribute("tabindex"));
      const explicitRestoreTarget = restoreFocusRef?.current;
      const restoreTarget = explicitRestoreTarget?.isConnected
        && !explicitRestoreTarget.closest("[inert]")
        && !explicitRestoreTarget.matches(":disabled")
        ? explicitRestoreTarget
        : previousCanReceiveFocus ? previouslyFocused : null;
      if (restoreTarget?.isConnected && !restoreTarget.closest("[inert]") && !restoreTarget.matches(":disabled")) {
        restoreTarget.focus({ preventScroll: true });
      }
    };
  }, [initialFocusRef, restoreFocusRef]);

  const requestBackdropClose = () => {
    if (topLayer() !== layerRef.current) return false;
    if (busyRef.current || !dismissOnBackdropRef.current) return false;
    const explicitRestoreTarget = restoreFocusRef?.current;
    onCloseRef.current();
    if (explicitRestoreTarget) {
      window.setTimeout(() => {
        if (explicitRestoreTarget.isConnected
          && !explicitRestoreTarget.closest("[inert]")
          && !explicitRestoreTarget.matches(":disabled")) {
          explicitRestoreTarget.focus({ preventScroll: true });
        }
      });
    }
    return true;
  };

  return (
    <div
      ref={layerRef}
      data-ui-dialog-layer="true"
      className={classes(
        "ui-dialog-backdrop",
        `ui-dialog-backdrop--${presentation}`,
      )}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestBackdropClose();
      }}
      onTouchEnd={(event) => {
        if (event.target !== event.currentTarget) return;
        if (requestBackdropClose()) event.preventDefault();
      }}
    >
      <section
        ref={surfaceRef}
        className={classes(
          "ui-dialog-surface",
          `ui-dialog-surface--${presentation}`,
          `ui-dialog-surface--${size}`,
          className,
        )}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={hasDescription ? descriptionId : undefined}
        aria-busy={busy || undefined}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="ui-dialog-head">
          <div className="ui-dialog-heading-copy">
            {eyebrow !== undefined && <div className="small muted ui-dialog-eyebrow">{eyebrow}</div>}
            <h2 id={titleId} className="ui-dialog-title">{title}</h2>
            {hasDescription && (
              <div id={descriptionId} className="small muted ui-dialog-description">
                {description}
              </div>
            )}
          </div>
          {showCloseButton && (
            <button
              ref={closeButtonRef}
              aria-label={closeLabel}
              className="quiet ui-dialog-close"
              type="button"
              disabled={busy}
              onClick={() => {
                if (!busyRef.current) onCloseRef.current();
              }}
            >
              {closeButtonText}
            </button>
          )}
        </div>
        <DialogSurfacePortalContext.Provider value={surfaceRef}>
          {children}
        </DialogSurfacePortalContext.Provider>
      </section>
    </div>
  );
}

export type DrawerProps = Omit<DialogProps, "presentation">;

export function Drawer(props: DrawerProps) {
  return <Dialog {...props} presentation="drawer"><ActionConfirmationHost forwardOnUnmount reveal>{props.children}</ActionConfirmationHost></Dialog>;
}

export interface ConfirmDialogProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  confirmingLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  /** Surface a rejected confirmation without leaving an unhandled promise. */
  onConfirmError?: (error: unknown) => void;
  onClose: () => void;
  busy?: boolean;
  dismissOnBackdrop?: boolean;
  dismissOnEscape?: boolean;
  confirmClassName?: string;
}

function reportConfirmError(error: unknown) {
  console.error("ConfirmDialog confirmation failed.", error);
}

export function ConfirmDialog({
  title,
  description,
  children,
  confirmLabel,
  confirmingLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onConfirmError = reportConfirmError,
  onClose,
  busy = false,
  dismissOnBackdrop = true,
  dismissOnEscape = true,
  confirmClassName,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  const confirm = () => {
    try {
      void Promise.resolve(onConfirm()).catch(onConfirmError);
    } catch (error) {
      onConfirmError(error);
    }
  };

  return (
    <Dialog
      title={title}
      description={description}
      onClose={onClose}
      initialFocusRef={cancelRef}
      size="small"
      role="alertdialog"
      showCloseButton={false}
      busy={busy}
      dismissOnBackdrop={dismissOnBackdrop}
      dismissOnEscape={dismissOnEscape}
    >
      {children}
      <div className="ui-confirm-actions">
        <button
          ref={cancelRef}
          className="action-secondary"
          type="button"
          disabled={busy}
          onClick={onClose}
        >
          {cancelLabel}
        </button>
        <button
          className={confirmClassName ?? "action-primary"}
          type="button"
          disabled={busy}
          onClick={confirm}
        >
          {busy && confirmingLabel ? confirmingLabel : confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}
